import { useContext, useMemo } from 'react';

import { TDocument, TDraft, TVariant, TVersion } from '../types';

import { resolveApprovedVersionId, selectVersion } from '@shared/helpers';
import { useSearchParams } from '@shared/hooks';
import { AccessType, TPermissions } from '@modules/access';
import { USER_GROUPS, SignInContext } from '@modules/users';
import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '@modules/inlineBlocks';

type TReturnUseView = {
  view: string | null;
  requestEdit: boolean;
  version?: TDraft | TVersion | TInlineBlockVersion | TInlineBlockDraft;
  variant?: TVariant;
};

export const getVariant = (
  variantFolderId?: string,
  version?: TDraft | TVersion | TInlineBlockDraft | TInlineBlockVersion
): TVariant | undefined => {
  let variant: TVariant | undefined = undefined;
  // TODO: remove when variants are implemented for inline-blocks
  if (version && !('variants' in version)) return undefined;

  if (version && variantFolderId && variantFolderId !== 'default')
    variant = version.variants.find((v) => v.folder === variantFolderId);
  if (!variant && version && !version.hasDefaultVariant) variant = version.variants[0];
  return variant;
};

const useView = (
  doc: TDocument | TInlineBlock,
  permissions: TPermissions,
  isEdit: boolean,
  retried: boolean
): TReturnUseView => {
  const { user } = useContext(SignInContext);

  const { searchParams } = useSearchParams();
  const view = searchParams.get('view');
  const variantFolderId = searchParams.get('variant');

  return useMemo(() => {
    // When we're forcing edit mode (isEdit) or retrying after an edit failure,
    // always target the draft - the caller wants to write to it.
    let requestEdit = true;
    let version: TDraft | TVersion | TInlineBlockDraft | TInlineBlockVersion | undefined = doc.draft;

    if (!isEdit && !retried) {
      // Cast to loose union shapes so `selectVersion` can run across
      // document and inline-block variants uniformly. The per-item type
      // guards elsewhere in the page narrow the result back correctly.
      type TAnyDraft = TDraft | TInlineBlockDraft;
      type TAnyVersion = (TVersion | TInlineBlockVersion) & { id: string };
      const enableApprovedView = 'approvals' in doc;
      const approvedVersionId = enableApprovedView
        ? resolveApprovedVersionId({
            versions: doc.versions,
            approvedVersionId: doc.stats.approvedVersionId,
            approvals: doc.approvals,
          })
        : undefined;

      version = selectVersion<TAnyDraft, TAnyVersion>({
        draft: doc.draft as TAnyDraft | undefined,
        versions: doc.versions as TAnyVersion[],
        view,
        approvedVersionId,
        enableApprovedView,
        canEdit: permissions.accessRight >= AccessType.EDIT,
        isAuthor: user.groups.includes(USER_GROUPS.AUTHOR),
      });
      requestEdit = view === 'edit';
    }

    const variant = getVariant(variantFolderId || undefined, version);

    return { view, requestEdit, version, variant };
  }, [
    view,
    variantFolderId,
    doc.draft,
    doc.versions,
    doc.stats.approvedVersionId,
    'approvals' in doc ? doc.approvals : undefined,
    permissions,
    isEdit,
  ]);
};

export default useView;
